import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, addMinutes } from "date-fns";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";


interface DateTimePickerProps {
  date: Date | undefined;
  time: string;
  onDateChange: (date: Date | undefined) => void;
  onTimeChange: (time: string) => void;
  label: string;
  isDateDisabled?: (date: Date) => boolean;
  useCurrentTimePlusFifteen?: boolean;
}

export const DateTimePicker = ({
  date,
  time,
  onDateChange,
  onTimeChange,
  label,
  isDateDisabled,
  useCurrentTimePlusFifteen = false,
}: DateTimePickerProps) => {
  useEffect(() => {
    if (time === "" || time === "08:00") {
      if (useCurrentTimePlusFifteen) {
        const now = new Date();
        const futureTime = addMinutes(now, 3);
        console.log(now);
        console.log(futureTime);
        const formattedTime = format(futureTime, "HH:mm");
        onTimeChange(formattedTime);
      } else {
        onTimeChange("08:00");
      }
    }
  }, []); // Run once on component mount

  const { t } = useTranslation();

  const disableDate = (date: Date) => {
    // First check the custom isDateDisabled function if provided
    if (isDateDisabled && isDateDisabled(date)) {
      return true;
    }

    // Get today's date with time set to midnight for comparison
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get the input date with time set to midnight for comparison
    const inputDate = new Date(date);
    inputDate.setHours(0, 0, 0, 0);

    // Disable dates before today (allow today)
    return inputDate < today;
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full sm:w-[220px] bg-white justify-start text-left text-[#34375B] font-normal hover:text-[#34375B] hover:bg-white",
              !date && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "PPP") : <span>{t("schedule_dialog.pick_a_date", "Pick a date")}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-background"
          align="start"
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={onDateChange}
            disabled={disableDate}
            initialFocus
          />
        </PopoverContent>
      </Popover>

      <div className="flex items-center gap-2 w-full sm:w-auto">
        <Clock className="h-4 w-4 text-gray-500" />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full sm:w-32 bg-white justify-start text-left text-[#34375B] font-normal"
            >
              {time || "Select time"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[180px] p-0 bg-background" align="start">
            <div className="grid grid-cols-2 gap-1 p-2">
              <div className="space-y-1 pr-1 border-r">
                <p className="text-xs font-semibold text-[#34375B] text-center">{t("schedule_dialog.hour", "Hour")}</p>
                <div
                  className="h-[120px] overflow-y-auto pr-1 custom-scrollbar"
                  onWheel={(e) => {
                    const container = e.currentTarget;
                    container.scrollTop += e.deltaY;
                    e.preventDefault();
                  }}
                >
                  {Array.from({ length: 24 }).map((_, hour) => (
                    <Button
                      key={hour}
                      variant="ghost"
                      size="sm"
                      className={`w-full justify-center font-normal text-[#34375B] text-sm py-1 ${time.split(':')[0] === hour.toString().padStart(2, '0')
                          ? 'bg-primary/20'
                          : ''
                        }`}
                      onClick={() => {
                        const [_, minute] = time.split(':');
                        onTimeChange(`${hour.toString().padStart(2, '0')}:${minute || '00'}`);
                      }}
                    >
                      {hour.toString().padStart(2, '0')}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-1 pl-1">
                <p className="text-xs font-semibold text-[#34375B] text-center">{t("schedule_dialog.minute", "Minute")}</p>
                <div
                  className="h-[120px] overflow-y-auto pl-1 custom-scrollbar"
                  onWheel={(e) => {
                    const container = e.currentTarget;
                    container.scrollTop += e.deltaY;
                    e.preventDefault();
                  }}
                >
                  {Array.from({ length: 12 }).map((_, i) => {
                    const minute = i * 5;
                    return (
                      <Button
                        key={minute}
                        variant="ghost"
                        size="sm"
                        className={`w-full justify-center font-normal text-[#34375B] text-sm py-1 ${time.split(':')[1] === minute.toString().padStart(2, '0')
                            ? 'bg-primary/20'
                            : ''
                          }`}
                        onClick={() => {
                          const [hour, _] = time.split(':');
                          onTimeChange(`${hour || '00'}:${minute.toString().padStart(2, '0')}`);
                        }}
                      >
                        {minute.toString().padStart(2, '0')}
                      </Button>
                    );
                  })}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

